/**
 * DiceAssetManager — Loads, caches, and pools custom dice models.
 *
 * Three-tier cache:
 *   L1: documentCache  — DiceAssetBundle (document + model info) per dice ID
 *   L2: templateCache  — Parsed GLB template (THREE.Group) per model code
 *   L3: pool           — Reusable cloned meshes per model code
 *
 * Inspired by PhoneDice DiceManager: load GLB once, clone from template,
 * apply materials per-instance, pool released dice for reuse.
 */

import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import type { DiceAssetBundle, MaterialSlotConfig } from '@sdl/shared';
import { buildMaterial, disposeMaterial, clearTextureCache } from './materialBuilder';

// ── Types ───────────────────────────────────────────────────────

export interface DiceAssetManagerConfig {
  cdnBase: string;
  apiBaseUrl: string;
}

/** Returned by acquireDice — everything needed to create a Dice entity */
export interface DiceAsset {
  diceId: string;
  modelCode: string;
  mesh: THREE.Group;
  halfExtents: [number, number, number];
  faceMap: number[];
  shaderMaterials: THREE.ShaderMaterial[];
  /** Cached physics body — created on first use, reused across pool cycles */
  body: import('cannon-es').Body | null;
}

/** Model metadata from /api/dice-models (cached at startup) */
export interface DiceModelInfo {
  code: string;
  faces: number[];
  glbUrl: string;
  physicsBox?: { halfExtents: [number, number, number] };
}

interface GlbTemplate {
  scene: THREE.Group;
  /** slotName → mesh names (for matching after clone) */
  slotMap: Map<string, string[]>;
  halfExtents: [number, number, number];
}

// ── Slot Detection ──────────────────────────────────────────────

function detectSlotName(name: string): string | null {
  const lower = name.toLowerCase();
  if (lower.includes('body')) return 'body';
  if (lower.includes('pip') || lower.includes('circle')) return 'pips';
  if (lower.includes('image') || lower.includes('decal')) return 'image';
  if (lower.includes('ring') || lower.includes('border')) return 'ring';
  if (lower.includes('face') && (lower.includes('1') || lower.includes('one'))) return 'face1';
  return null;
}

// ── DiceAssetManager ────────────────────────────────────────────

const MAX_POOL_SIZE_PER_MODEL = 6;
const gltfLoader = new GLTFLoader();

export class DiceAssetManager {
  private apiBaseUrl: string;

  // L1: DiceAssetBundle cache (document + model info) keyed by diceId
  private documentCache = new Map<string, DiceAssetBundle>();

  // L2: Parsed GLB template keyed by model code (e.g. "DSS01")
  private templateCache = new Map<string, GlbTemplate>();

  // L3: Pool of released DiceAssets keyed by model code
  private pool = new Map<string, DiceAsset[]>();

  // In-flight fetch dedup: prevents duplicate fetches for the same model
  private glbFetchPromises = new Map<string, Promise<GlbTemplate>>();
  private apiFetchPromises = new Map<string, Promise<void>>();

  // Track all active shader materials for time updates
  private activeShaderMaterials = new Set<THREE.ShaderMaterial>();

  /** Model metadata from /api/dice-models (faces, physicsBox, glbUrl per code) */
  private modelMetadata = new Map<string, DiceModelInfo>();

  constructor(config: DiceAssetManagerConfig) {
    this.apiBaseUrl = config.apiBaseUrl;
  }

  // ── Startup Preload ─────────────────────────────────────────

  /**
   * Preload ALL dice GLB models at app startup (PhoneDice DiceManager pattern).
   * Fetches the model catalog, downloads every GLB in parallel, and stores
   * templates in cache. After this, acquireDice only needs the document (skin)
   * — the heavy GLB is already parsed and ready to clone.
   */
  async preloadAllModels(): Promise<void> {
    try {
      const resp = await fetch(`${this.apiBaseUrl}/api/dice-models`);
      if (!resp.ok) {
        console.warn('[DiceAssetManager] Failed to fetch dice models catalog:', resp.status);
        return;
      }
      const models: DiceModelInfo[] = await resp.json();

      for (const model of models) {
        this.modelMetadata.set(model.code, model);
      }

      const uncached = models.filter((m) => !this.templateCache.has(m.code));
      if (uncached.length === 0) {
        console.log(`[DiceAssetManager] All ${models.length} models already cached`);
        return;
      }

      // Sequential, NOT Promise.all. Loading every model in parallel fires all
      // fetches at once and then parses + GPU-uploads them back-to-back with no
      // gaps — one long unbroken block that freezes the thread, starves the
      // socket heartbeat (→ disconnect), and slams the GPU all at once (which
      // compounds across refreshes). One at a time lets the thread breathe
      // during each fetch: the loader animates, the heartbeat fires, and
      // textures upload one-per-frame instead of in a single burst. We still
      // load them all before boot continues.
      for (const m of uncached) {
        try {
          await this.fetchGlb(m.code, m.glbUrl);
        } catch (err) {
          console.warn(`[DiceAssetManager] model ${m.code} preload failed:`, err);
        }
      }

      console.log(`[DiceAssetManager] Preloaded ${uncached.length} dice models (${models.length} total)`);
    } catch (err) {
      console.warn('[DiceAssetManager] preloadAllModels error:', err);
    }
  }

  /** Get cached model metadata by code */
  getModelInfo(code: string): DiceModelInfo | undefined {
    return this.modelMetadata.get(code);
  }

  // ── Per-Dice Preload ────────────────────────────────────────

  /**
   * Preload assets for a list of dice IDs.
   * Fetches DiceDocuments from API, then GLBs from CDN.
   * Safe to call multiple times — deduplicates in-flight requests.
   */
  async preload(diceIds: string[]): Promise<void> {
    // Filter out already-cached IDs
    const uncached = diceIds.filter((id) => id && !this.documentCache.has(id));
    if (uncached.length === 0) return;

    // Deduplicate API fetch
    const cacheKey = uncached.sort().join(',');
    if (!this.apiFetchPromises.has(cacheKey)) {
      this.apiFetchPromises.set(cacheKey, this.fetchDocuments(uncached));
    }
    await this.apiFetchPromises.get(cacheKey);
    this.apiFetchPromises.delete(cacheKey);

    // Fetch GLBs for all unique models
    const modelCodes = new Set<string>();
    for (const id of diceIds) {
      const bundle = this.documentCache.get(id);
      if (bundle && !this.templateCache.has(bundle.model.code)) {
        modelCodes.add(bundle.model.code);
      }
    }

    await Promise.all(
      [...modelCodes].map((code) => this.fetchGlb(code))
    );
  }

  /** Fetch DiceAssetBundles from the batch API */
  private async fetchDocuments(diceIds: string[]): Promise<void> {
    try {
      const url = `${this.apiBaseUrl}/api/dice-assets?ids=${diceIds.join(',')}`;
      const resp = await fetch(url);
      if (!resp.ok) {
        console.warn(`[DiceAssetManager] API fetch failed: ${resp.status}`);
        return;
      }
      const data = await resp.json();
      const assets: DiceAssetBundle[] = data.assets ?? [];
      for (const bundle of assets) {
        this.documentCache.set(bundle.diceId, bundle);
      }
    } catch (err) {
      console.warn('[DiceAssetManager] API fetch error:', err);
    }
  }

  /** Fetch and parse a GLB from CDN, store as template */
  private async fetchGlb(modelCode: string, directUrl?: string): Promise<GlbTemplate> {
    if (this.templateCache.has(modelCode)) {
      return this.templateCache.get(modelCode)!;
    }

    // Dedup in-flight GLB fetches
    if (this.glbFetchPromises.has(modelCode)) {
      return this.glbFetchPromises.get(modelCode)!;
    }

    const promise = this.loadGlbTemplate(modelCode, directUrl);
    this.glbFetchPromises.set(modelCode, promise);

    try {
      const template = await promise;
      this.templateCache.set(modelCode, template);
      return template;
    } finally {
      this.glbFetchPromises.delete(modelCode);
    }
  }

  /** Load GLB ArrayBuffer and parse into template */
  private async loadGlbTemplate(modelCode: string, directUrl?: string): Promise<GlbTemplate> {
    // Use direct URL (from preloadAllModels) or find from cached bundles
    let glbUrl = directUrl ?? '';
    if (!glbUrl) {
      for (const bundle of this.documentCache.values()) {
        if (bundle.model.code === modelCode) {
          glbUrl = bundle.model.glbUrl;
          break;
        }
      }
    }
    // Fall back to model metadata (from preloadAllModels catalog)
    if (!glbUrl) {
      const meta = this.modelMetadata.get(modelCode);
      if (meta) glbUrl = meta.glbUrl;
    }

    if (!glbUrl) {
      throw new Error(`[DiceAssetManager] No GLB URL for model ${modelCode}`);
    }

    // Fetch GLB as ArrayBuffer. The base models are SHIPPED with the client
    // (client/public/assets/dice-models/<code>.glb) — a fixed set that never
    // changes — so we read them locally for instant, offline, network-free
    // loading. Any model NOT in the bundle (e.g. one added after this build)
    // falls back to the CDN url, so the catalog stays flexible and the editor /
    // other apps keep using the CDN unchanged.
    const localUrl = `${import.meta.env.BASE_URL}assets/dice-models/${modelCode}.glb`;
    let arrayBuffer: ArrayBuffer;
    let source = 'local';
    const tFetch0 = performance.now();
    try {
      const localResp = await fetch(localUrl);
      if (!localResp.ok) throw new Error(`local ${localResp.status}`);
      arrayBuffer = await localResp.arrayBuffer();
    } catch {
      source = 'CDN';
      const resp = await fetch(glbUrl);
      if (!resp.ok) throw new Error(`GLB fetch failed: ${resp.status} for ${glbUrl}`);
      arrayBuffer = await resp.arrayBuffer();
    }
    const tFetch1 = performance.now();

    // Parse with GLTFLoader
    const tParse0 = performance.now();
    const gltf = await new Promise<any>((resolve, reject) => {
      gltfLoader.parse(arrayBuffer, '', resolve, reject);
    });
    const tParse1 = performance.now();
    console.log(
      `[BOOT] glb "${modelCode}" (${(arrayBuffer.byteLength / 1024).toFixed(0)}KB ${source}): ` +
      `fetch=${(tFetch1 - tFetch0).toFixed(0)}ms  parse=${(tParse1 - tParse0).toFixed(0)}ms`,
    );

    const scene: THREE.Group = gltf.scene;

    // Detect slot meshes by material/mesh name
    const slotMap = new Map<string, string[]>();
    const detectedNames = new Set<string>();

    scene.traverse((node: THREE.Object3D) => {
      if (!(node as THREE.Mesh).isMesh) return;
      const mesh = node as THREE.Mesh;
      const matName = (mesh.material as THREE.Material)?.name || '';
      const meshName = mesh.name || '';
      const slotName = detectSlotName(matName) || detectSlotName(meshName) || matName || meshName || null;

      if (slotName) {
        if (!slotMap.has(slotName)) slotMap.set(slotName, []);
        slotMap.get(slotName)!.push(mesh.name || mesh.uuid);
        detectedNames.add(slotName);
      }
    });

    // Fallback: treat entire model as "body"
    if (detectedNames.size === 0) {
      scene.traverse((node: THREE.Object3D) => {
        if ((node as THREE.Mesh).isMesh) {
          if (!slotMap.has('body')) slotMap.set('body', []);
          slotMap.get('body')!.push(node.name || node.uuid);
          detectedNames.add('body');
        }
      });
    }

    // Center the model
    const box = new THREE.Box3().setFromObject(scene);
    const center = box.getCenter(new THREE.Vector3());
    scene.position.sub(center);

    // Compute half-extents after centering
    const centeredBox = new THREE.Box3().setFromObject(scene);
    const size = centeredBox.getSize(new THREE.Vector3());
    const halfExtents: [number, number, number] = [
      parseFloat((size.x / 2).toFixed(4)),
      parseFloat((size.y / 2).toFixed(4)),
      parseFloat((size.z / 2).toFixed(4)),
    ];

    return { scene, slotMap, halfExtents };
  }

  // ── Query ───────────────────────────────────────────────────

  /** Check if a dice ID is fully loaded (document + GLB template) */
  isReady(diceId: string): boolean {
    const bundle = this.documentCache.get(diceId);
    if (!bundle) return false;
    return this.templateCache.has(bundle.model.code);
  }

  // ── Acquire / Release ───────────────────────────────────────

  /**
   * Get a ready-to-use dice mesh. Returns null if not loaded.
   * Pool hit: reuse existing clone + re-apply materials.
   * Pool miss: clone from template + apply materials.
   */
  acquireDice(diceId: string): DiceAsset | null {
    const bundle = this.documentCache.get(diceId);
    if (!bundle) return null;

    const template = this.templateCache.get(bundle.model.code);
    if (!template) return null;

    // Physics box: prefer server-stored, fall back to computed from GLB
    const halfExtents = bundle.model.physicsBox?.halfExtents ?? template.halfExtents;
    const faceMap = bundle.model.faces;

    // Try pool first
    const poolKey = bundle.model.code;
    const poolArr = this.pool.get(poolKey);
    if (poolArr && poolArr.length > 0) {
      const asset = poolArr.pop()!;
      asset.diceId = diceId;
      // Re-apply materials for the new skin
      this.applyMaterials(asset.mesh, bundle.document.slots, template.slotMap);
      asset.halfExtents = halfExtents;
      asset.faceMap = faceMap;
      asset.shaderMaterials = this.collectShaderMaterials(asset.mesh);
      for (const sm of asset.shaderMaterials) this.activeShaderMaterials.add(sm);
      asset.mesh.visible = true;
      return asset;
    }

    // Clone from template
    const mesh = template.scene.clone(true);

    // Clone materials so each instance is independent
    mesh.traverse((node: THREE.Object3D) => {
      if (!(node as THREE.Mesh).isMesh) return;
      const m = node as THREE.Mesh;
      if (Array.isArray(m.material)) {
        m.material = m.material.map((mat) => mat.clone());
      } else {
        m.material = m.material.clone();
      }
    });

    // Apply DiceDocument materials
    this.applyMaterials(mesh, bundle.document.slots, template.slotMap);

    // Dice CAST shadows onto the table but do NOT receive them. Self-shadowing
    // a small faceted die (body, pip discs, etc.) produces shadow-acne speckle
    // on the bright pips — invisible on desktop's 24-bit shadow sampling, ugly
    // on the Android WebView's lower-precision sampling. receiveShadow=false on
    // every dice mesh part removes that self-shadow entirely; the cast shadow
    // onto the floor (the one that actually reads) is unaffected.
    mesh.traverse((node: THREE.Object3D) => {
      if ((node as THREE.Mesh).isMesh) {
        node.castShadow = true;
        node.receiveShadow = false;
      }
    });

    const shaderMaterials = this.collectShaderMaterials(mesh);
    for (const sm of shaderMaterials) this.activeShaderMaterials.add(sm);

    return {
      diceId,
      modelCode: bundle.model.code,
      mesh,
      halfExtents,
      faceMap,
      shaderMaterials,
      body: null,
    };
  }

  /** Return a dice to the pool for reuse */
  releaseDice(asset: DiceAsset): void {
    // Remove shader materials from active set
    for (const sm of asset.shaderMaterials) {
      this.activeShaderMaterials.delete(sm);
    }

    asset.mesh.visible = false;
    asset.mesh.parent?.remove(asset.mesh);

    const poolKey = asset.modelCode;
    if (!this.pool.has(poolKey)) this.pool.set(poolKey, []);
    const poolArr = this.pool.get(poolKey)!;

    // Duplicate guard + size limit
    if (poolArr.indexOf(asset) < 0 && poolArr.length < MAX_POOL_SIZE_PER_MODEL) {
      poolArr.push(asset);
    } else {
      // Pool full — dispose
      this.disposeAssetMeshes(asset);
    }
  }

  // ── Material Application ────────────────────────────────────

  /** Apply DiceDocument slot materials to a cloned mesh */
  private applyMaterials(
    mesh: THREE.Group,
    slots: Record<string, MaterialSlotConfig>,
    slotMap: Map<string, string[]>,
  ): void {
    for (const [slotName, config] of Object.entries(slots)) {
      const meshNames = slotMap.get(slotName);
      if (!meshNames) continue;

      const newMat = buildMaterial(config);

      mesh.traverse((node: THREE.Object3D) => {
        if (!(node as THREE.Mesh).isMesh) return;
        const m = node as THREE.Mesh;
        const nameMatch = meshNames.includes(m.name) || meshNames.includes(m.uuid);

        // Also match by material name or slot detection
        if (!nameMatch) {
          const matName = (m.material as THREE.Material)?.name || '';
          const meshSlot = detectSlotName(matName) || detectSlotName(m.name);
          if (meshSlot !== slotName) return;
        }

        // Dispose old material and assign new
        if (Array.isArray(m.material)) {
          m.material.forEach((mat) => disposeMaterial(mat));
          m.material = [newMat.clone()];
        } else {
          disposeMaterial(m.material);
          m.material = newMat.clone();
        }
      });

      // Dispose the template material (clones were assigned)
      disposeMaterial(newMat);
    }
  }

  /** Collect all ShaderMaterial instances from a mesh tree */
  private collectShaderMaterials(mesh: THREE.Object3D): THREE.ShaderMaterial[] {
    const result: THREE.ShaderMaterial[] = [];
    mesh.traverse((node: THREE.Object3D) => {
      if (!(node as THREE.Mesh).isMesh) return;
      const mats = Array.isArray((node as THREE.Mesh).material)
        ? (node as THREE.Mesh).material as THREE.Material[]
        : [(node as THREE.Mesh).material as THREE.Material];
      for (const mat of mats) {
        if ((mat as THREE.ShaderMaterial).isShaderMaterial && (mat as THREE.ShaderMaterial).uniforms?.time) {
          result.push(mat as THREE.ShaderMaterial);
        }
      }
    });
    return result;
  }

  // ── Per-Frame Update ────────────────────────────────────────

  /** Update time uniform on all active shader materials */
  updateShaderTime(time: number): void {
    for (const sm of this.activeShaderMaterials) {
      sm.uniforms.time.value = time;
    }
  }

  // ── Cleanup ─────────────────────────────────────────────────

  private disposeAssetMeshes(asset: DiceAsset): void {
    asset.mesh.traverse((node: THREE.Object3D) => {
      if (!(node as THREE.Mesh).isMesh) return;
      const m = node as THREE.Mesh;
      if (Array.isArray(m.material)) {
        m.material.forEach((mat) => mat.dispose());
      } else {
        m.material.dispose();
      }
      // Don't dispose geometry — it's shared with the template
    });
  }

  /** Dispose all caches, templates, and pooled instances */
  dispose(): void {
    // Dispose pooled assets
    for (const poolArr of this.pool.values()) {
      for (const asset of poolArr) {
        this.disposeAssetMeshes(asset);
      }
    }
    this.pool.clear();

    // Dispose templates (geometry + materials)
    for (const template of this.templateCache.values()) {
      template.scene.traverse((node: THREE.Object3D) => {
        if (!(node as THREE.Mesh).isMesh) return;
        const m = node as THREE.Mesh;
        m.geometry?.dispose();
        if (Array.isArray(m.material)) {
          m.material.forEach((mat) => mat.dispose());
        } else {
          m.material?.dispose();
        }
      });
    }
    this.templateCache.clear();

    this.documentCache.clear();
    this.activeShaderMaterials.clear();
    this.glbFetchPromises.clear();
    this.apiFetchPromises.clear();

    clearTextureCache();
  }
}
